/**
 * Flat tree for the Robotics tab.
 *
 * Controllers are roots (or nest under a SUPERVISES master), arms hang
 * off controllers via HOSTS, and jobs hang off arms via HAS_JOB.
 * Signals and conditions are not tree nodes; their counts roll into
 * the owning controller / arm / job rows. Controller signal/condition
 * counts union `DEFINES` with the job path so DEFINES-only resources
 * are not invisible.
 *
 * Pipeline: emit bare node rows (no relationship fan-out) → ORDER BY /
 * SKIP / LIMIT → compute badge counts for the surviving page only.
 * Each count family is its own top-level CALL (no nested CALL — that
 * shape is rejected by Neo4j in this stack). Counts never
 * Cartesian-product across unrelated relationship families.
 */
export const PLANT_ROBOTICS_TREE_CYPHER = `
            CALL {
              MATCH (c:RobotController)
              RETURN count(c) AS totalControllerCount
            }
            CALL {
              MATCH (a:RobotArm)
              RETURN count(a) AS totalArmCount
            }
            CALL {
              MATCH (j:RobotJob)
              RETURN count(j) AS totalJobCount
            }
            CALL {
              MATCH (n:RobotController)
              OPTIONAL MATCH (mast:RobotController)-[:SUPERVISES]->(n)
              WITH n, head(collect(DISTINCT mast.id)) AS parentId
              RETURN n AS node,
                     parentId,
                     'RobotController' AS kind

              UNION ALL

              MATCH (parent:RobotController)-[:HOSTS]->(n:RobotArm)
              RETURN n AS node,
                     parent.id AS parentId,
                     'RobotArm' AS kind

              UNION ALL

              MATCH (parent:RobotArm)-[:HAS_JOB]->(n:RobotJob)
              RETURN n AS node,
                     parent.id AS parentId,
                     'RobotJob' AS kind
            }
            WITH totalControllerCount, totalArmCount, totalJobCount, node, parentId, kind
            ORDER BY CASE kind
                       WHEN 'RobotController' THEN 0
                       WHEN 'RobotArm' THEN 1
                       ELSE 2
                     END,
                     toLower(coalesce(node.display_name, node.name, node.id))
            SKIP toInteger($skip) LIMIT toInteger($pageSize)
            WITH totalControllerCount, totalArmCount, totalJobCount, node, parentId, kind

            CALL {
              WITH node, kind
              OPTIONAL MATCH (node)-[:HOSTS]->(a:RobotArm)
                WHERE kind = 'RobotController'
              RETURN count(DISTINCT a) AS armCount
            }

            CALL {
              WITH node, kind
              OPTIONAL MATCH (node)-[:HOSTS]->(:RobotArm)-[:HAS_JOB]->(ctrlJob:RobotJob)
                WHERE kind = 'RobotController'
              WITH node, kind, count(DISTINCT ctrlJob) AS ctrlJobCount
              OPTIONAL MATCH (node)-[:HAS_JOB]->(armJob:RobotJob)
                WHERE kind = 'RobotArm'
              WITH kind, ctrlJobCount, count(DISTINCT armJob) AS armJobCount
              RETURN CASE kind
                       WHEN 'RobotController' THEN ctrlJobCount
                       WHEN 'RobotArm' THEN armJobCount
                       ELSE 0
                     END AS jobCount
            }

            CALL {
              WITH node, kind
              OPTIONAL MATCH (node)-[:HOSTS]->(:RobotArm)-[:HAS_JOB]->(ctrlJob:RobotJob)
                WHERE kind = 'RobotController'
              OPTIONAL MATCH (sigJob:RobotIOSignal)-[:READ_BY|WRITTEN_BY]->(ctrlJob)
              WITH node, kind, collect(DISTINCT sigJob.id) AS jobPathIds
              OPTIONAL MATCH (node)-[:DEFINES]->(sigDef:RobotIOSignal)
                WHERE kind = 'RobotController'
              WITH node, kind, jobPathIds, collect(DISTINCT sigDef.id) AS defIds
              WITH node, kind,
                   [id IN jobPathIds + defIds WHERE id IS NOT NULL] AS ctrlMerged
              OPTIONAL MATCH (node)-[:HAS_JOB]->(armJob:RobotJob)
                WHERE kind = 'RobotArm'
              OPTIONAL MATCH (armSig:RobotIOSignal)-[:READ_BY|WRITTEN_BY]->(armJob)
              WITH node, kind, ctrlMerged, collect(DISTINCT armSig.id) AS armIds
              OPTIONAL MATCH (jobSig:RobotIOSignal)-[:READ_BY|WRITTEN_BY]->(node)
                WHERE kind = 'RobotJob'
              WITH kind, ctrlMerged, armIds, collect(DISTINCT jobSig.id) AS jobIds
              UNWIND (CASE WHEN size(ctrlMerged) = 0 THEN [null] ELSE ctrlMerged END) AS ctrlId
              WITH kind, armIds, jobIds, collect(DISTINCT ctrlId) AS ctrlUnique
              RETURN CASE kind
                       WHEN 'RobotController' THEN size([id IN ctrlUnique WHERE id IS NOT NULL])
                       WHEN 'RobotArm' THEN size([id IN armIds WHERE id IS NOT NULL])
                       WHEN 'RobotJob' THEN size([id IN jobIds WHERE id IS NOT NULL])
                       ELSE 0
                     END AS signalCount
            }

            CALL {
              WITH node, kind
              OPTIONAL MATCH (node)-[:HOSTS]->(:RobotArm)-[:HAS_JOB]->(ctrlJob:RobotJob)
                WHERE kind = 'RobotController'
              OPTIONAL MATCH (condJob:RobotCondition)-[:USED_BY]->(ctrlJob)
              WITH node, kind, collect(DISTINCT condJob.id) AS jobPathIds
              OPTIONAL MATCH (node)-[:DEFINES]->(condDef:RobotCondition)
                WHERE kind = 'RobotController'
              WITH node, kind, jobPathIds, collect(DISTINCT condDef.id) AS defIds
              WITH node, kind,
                   [id IN jobPathIds + defIds WHERE id IS NOT NULL] AS ctrlMerged
              OPTIONAL MATCH (node)-[:HAS_JOB]->(armJob:RobotJob)
                WHERE kind = 'RobotArm'
              OPTIONAL MATCH (armCond:RobotCondition)-[:USED_BY]->(armJob)
              WITH node, kind, ctrlMerged, collect(DISTINCT armCond.id) AS armIds
              OPTIONAL MATCH (jobCond:RobotCondition)-[:USED_BY]->(node)
                WHERE kind = 'RobotJob'
              WITH kind, ctrlMerged, armIds, collect(DISTINCT jobCond.id) AS jobIds
              UNWIND (CASE WHEN size(ctrlMerged) = 0 THEN [null] ELSE ctrlMerged END) AS ctrlId
              WITH kind, armIds, jobIds, collect(DISTINCT ctrlId) AS ctrlUnique
              RETURN CASE kind
                       WHEN 'RobotController' THEN size([id IN ctrlUnique WHERE id IS NOT NULL])
                       WHEN 'RobotArm' THEN size([id IN armIds WHERE id IS NOT NULL])
                       WHEN 'RobotJob' THEN size([id IN jobIds WHERE id IS NOT NULL])
                       ELSE 0
                     END AS conditionCount
            }

            RETURN totalControllerCount,
                   totalArmCount,
                   totalJobCount,
                   node,
                   parentId,
                   kind,
                   armCount,
                   jobCount,
                   signalCount,
                   conditionCount
          `
